import { logEvent } from '@/pages/api/v1/utils';
import {
  addHubSpotEvent,
  HUBSPOT_EVENTS,
} from '@/utils/analytics/hubspot/add-hubspot-event';

/**
 * Video upload tracking utilities for ClickHouse analytics
 *
 * Tracks video upload events with file metadata and success/error status
 */

// File size constants for formatting
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;

// Regex for extracting codec from MIME type
const CODEC_REGEX = /codecs="([^"]+)"/;

export interface VideoFileMetadata {
  /** File extension extracted from name */
  extension: string;
  /** MIME type (e.g., 'video/mp4') */
  mimeType: string;
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
}

/**
 * Extract metadata from a File object
 */
export const extractFileMetadata = (file: File): VideoFileMetadata => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? 'unknown';

  return {
    size: file.size,
    mimeType: file.type || 'unknown',
    name: file.name,
    extension,
  };
};

/**
 * Format file size for logging (bytes to human-readable)
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < BYTES_PER_KB) {
    return `${bytes}B`;
  }
  if (bytes < BYTES_PER_MB) {
    return `${(bytes / BYTES_PER_KB).toFixed(1)}KB`;
  }
  return `${(bytes / BYTES_PER_MB).toFixed(1)}MB`;
};

/**
 * Try to detect video codec from video element
 * Note: This is not always reliable across browsers
 */
export const detectVideoCodec = (
  video: HTMLVideoElement
): string | undefined => {
  // Try to get codec info from videoTracks API (limited browser support)
  const videoWithTracks = video as HTMLVideoElement & {
    videoTracks?: { length: number; [index: number]: { id: string } };
  };

  if (videoWithTracks.videoTracks && videoWithTracks.videoTracks.length > 0) {
    return videoWithTracks.videoTracks[0]?.id;
  }

  // Fallback: infer from src/type if available
  const source = video.querySelector('source');
  if (source?.type) {
    // Extract codec from type like 'video/mp4; codecs="avc1.42E01E"'
    const codecMatch = CODEC_REGEX.exec(source.type);
    if (codecMatch) {
      return codecMatch[1];
    }
  }

  // No codec detected
  return;
};

/**
 * Track a video upload attempt
 * Called when user selects a file (before validation)
 */
export const trackVideoUploadAttempt = (
  file: File,
  asPath: string
): VideoFileMetadata => {
  const metadata = extractFileMetadata(file);

  void logEvent({
    event_type: 'clip_sync',
    asPath,
    metadata: {
      type: 'video_upload_attempt',
      popup_id: 'video-player',
      format: metadata.mimeType,
      text: metadata.extension,
      value: metadata.size,
    },
  });

  return metadata;
};

/**
 * Track a video upload validation error
 * Called when file fails validation (wrong format, etc.)
 */
export const trackVideoUploadError = (
  error: string,
  fileMetadata: VideoFileMetadata | null,
  asPath: string
): void => {
  void logEvent({
    event_type: 'error',
    asPath,
    metadata: {
      type: 'video_upload_error',
      popup_id: 'video-player',
      code: error,
      format: fileMetadata?.mimeType ?? 'unknown',
      text: fileMetadata
        ? `${fileMetadata.extension} (${formatFileSize(fileMetadata.size)})`
        : 'unknown',
      value: fileMetadata?.size,
    },
  });
};

/**
 * Track a successful video load
 * Called when video finishes loading and is ready to play
 */
export const trackVideoUploadSuccess = (
  fileMetadata: VideoFileMetadata,
  video: HTMLVideoElement,
  asPath: string
): void => {
  const codec = detectVideoCodec(video);
  const duration = Number.isFinite(video.duration) ? video.duration : undefined;

  void logEvent({
    event_type: 'clip_sync',
    asPath,
    metadata: {
      type: 'video_upload_success',
      popup_id: 'video-player',
      format: fileMetadata.mimeType,
      text: codec
        ? `${fileMetadata.extension} (${codec})`
        : fileMetadata.extension,
      value: fileMetadata.size,
      // Store additional data as JSON in a custom field
      // Duration is stored separately for easier querying
    },
  });

  addHubSpotEvent(HUBSPOT_EVENTS.uploadedFile, { file_type: 'video' });

  // Log duration as a separate event for easier analytics
  if (duration !== undefined) {
    void logEvent({
      event_type: 'clip_sync',
      asPath,
      metadata: {
        type: 'video_upload_duration',
        popup_id: 'video-player',
        value: Math.round(duration),
        text: `${video.videoWidth}x${video.videoHeight}`,
        format: fileMetadata.mimeType,
      },
    });
  }
};

/**
 * Track video load error (file selected but failed to load)
 * Called when the video element fires an error event
 */
export const trackVideoLoadError = (
  fileMetadata: VideoFileMetadata | null,
  asPath: string
): void => {
  void logEvent({
    event_type: 'error',
    asPath,
    metadata: {
      type: 'video_load_error',
      popup_id: 'video-player',
      code: 'Failed to load video',
      format: fileMetadata?.mimeType ?? 'unknown',
      text: fileMetadata
        ? `${fileMetadata.extension} (${formatFileSize(fileMetadata.size)})`
        : 'unknown',
      value: fileMetadata?.size,
    },
  });
};

/**
 * Track sync preview button click
 * Called when user clicks the sync preview button in track row or menu
 */
export const trackSyncPreviewClick = (
  asPath: string,
  source: 'three_dot_menu' | 'track_row' | 'player_bar' | 'customize_header'
): void => {
  void logEvent({
    event_type: 'clip_sync',
    asPath,
    metadata: {
      type: 'sync_preview_click',
      popup_id: 'video-player',
      text: source,
    },
  });

  addHubSpotEvent(HUBSPOT_EVENTS.usedSyncPreview, { source });
};
